
import Model from './Model'
import Modifier from './Modifier'
import OrderContentCombination from './OrderContentCombination'
import OrderContentType from './Static/OrderContentType'
import MenuItemType from './Static/MenuItemType'
import CalculatePrintStatus from './Static/OrderContentPrintStatus'
import AppliedTax from './AppliedTax'

export default class OrderContent extends Model {
  constructor (attributes = {}) {
    super(attributes.id)
    this._index = attributes.index
    this.type = attributes.type ?? OrderContentType.NORMAL // TINYINT
    this.order_content_id = attributes.order_content_id ?? null // INT
    this.alreadyPaidQuantity = attributes.alreadyPaidQuantity ?? 0 // INT
    this.alreadyPrintedQuantity = attributes.alreadyPrintedQuantity ?? 0 // INT
    this.cancelReason = attributes.cancelReason ?? null // VARCHAR 255
    this.discount = attributes.discount ?? null // VARCHAR 255 (JSON)
    this.dishOrder = attributes.dishOrder ?? 0 // INT
    this.format_pivot_id = attributes.format_pivot_id ?? null // INT
    this.itemName = attributes.itemName // VARCHAR 255
    this.itemPrice = parseFloat(attributes.itemPrice) // DECIMAL 8,2
    this.item_id = attributes.item_id // INT
    this.margin = parseFloat(attributes.margin) || null // DECIMAL 8,2
    this.notes = attributes.notes ?? null // VARCHAR 255
    this.optional_modifiers = attributes.optional_modifiers ?? null // TEXT (JSON)
    this.orderContentCombination = attributes.orderContentCombination
      ? new OrderContentCombination(attributes.orderContentCombination)
      : null
    this.order_id = attributes.order_id ?? null // INT
    this.price_id = attributes.price_id ?? null
    this.quantity = parseFloat(attributes.quantity ?? 1) // DECIMAL 8,4
    this.seat = attributes.seat ?? null // INT
    this.tenantUser_id = attributes.tenantUser_id ?? $vm.$store.state.user.id
    this.weight = parseFloat(attributes.weight) || null // DECIMAL 8,3

    this._setModifiers(attributes.modifiers) // TEXT (JSON)
    this._setSubContents(attributes.subContents) // self-relationship
    this._item = this._getItem(attributes.taxPercentage)

    this.appliedTaxes = attributes.appliedTaxes?.map(o => new AppliedTax(o)) ?? []

    this.recalculate()
    // this.discountAmount -> DECIMAL 10,4 recalculated
    // this.extrasAmount -> DECIMAL 8,2 recalculated
    // this.priceWithExtrasIndividual -> DECIMAL 8,2 recalculated
    // this.subtotal -> DECIMAL 10,4 recalculated
    // this.taxAmount -> DECIMAL 10,4 recalculated
    // this.taxPercentage -> DECIMAL 8,2 recalculated @deprecated
    // this.total -> DECIMAL 8,2 recalculated
  }

  _setModifiers (modifiers) {
    this.modifiers = modifiers?.map(o => new Modifier(o)) ?? []
  }

  _setSubContents (subContents) {
    this.subContents = subContents?.map(o => new OrderContent(o)) ?? []
  }

  _getItem (taxPercentage) {
    return $vm.$store.getters['catalog/item'](this.item_id) || {
      name: this.itemName,
      price: this.itemPrice,
      tax_id: $vm.$store.state.rummage.data.Tax?.find(o => o.percentage == taxPercentage)?.id,
    }
  }

  get index () {
    return this._index ?? 0
  }

  set index (v) {
    this._index = v
  }

  get item () {
    return this._item || (this._item = this._getItem(this.taxPercentage))
  }

  get name () {
    return this.format_pivot_id && !! $vm.$store.getters['orders/config']('appendSellingFormatName')
      ? `${this.sellingFormat?.name} ${this.itemName}`
      : this.itemName
  }

  get sellingFormatPivot () {
    return $vm.$store.getters['catalog/sellingFormatPivot'](this.format_pivot_id)
  }

  get sellingFormat () {
    return $vm.$store.getters['catalog/sellingFormat'](this.sellingFormatPivot?.format_id)
  }

  get taxes () {
    // copied from Xef's APP GSOrderContent.m REV-15384
    if (this._taxes) {
      return this._taxes
    }
    if (this.appliedTaxes.length > 0) {
      this._taxes = this.appliedTaxes
        .map(appliedTax => this._getTax(appliedTax.tax_id))
        .filter(o => !! o)
    }
    if (! this._taxes?.length) {
      this._taxes = [this.tax]
    }

    return this._taxes
  }

  get tax () {
    // inspired by XEF's APP
    if (this._tax) {
      return this._tax
    }

    if (!! this.appliedTaxes.length) {
      // take the first applied tax (backwards compatibility)
      this._tax = this._getTax(this.appliedTaxes.find(o => !! o.tax_id))
    }

    if (! this._tax) {
        this._tax = this._getTaxFromPercentage()
    }

    if (! this._tax) {
      this._tax = this._getTaxFromItem()
    }

    if (! this._tax) {
      const category = $vm.$store.getters['catalog/category'](this.item.category_id)

      this._tax = this._getTaxFromCategory(category) || this._getTaxFromGroup(category)
    }

    if (! this._tax) {
      this._tax = { id: null, percentage: 0 , name: '' }
    }

    return this._tax
  }

  _getTax (id) {
    return id ? $vm.$store.getters['rummage/tax'](id) : null
  }

  _getTaxFromPercentage () {
    return !! this.taxPercentage
      ? $vm.$store.state.rummage.data.Tax?.find(o => o.percentage == this.taxPercentage)
      : null
  }

  _getTaxFromItem () {
    return this._getTax(this.item.tax_id)
  }

  _getTaxFromCategory (category) {
    return this._getTax(category?.tax_id)
  }

  _getTaxFromGroup (category) {
    return category
      ? this._getTax($vm.$store.getters['catalog/group'](category.group_id)?.tax_id)
      : null
  }

  get stringHash () {
    return [
      this.itemName.toLowerCase().withoutDiacritic(),
      this.item_id,
      this.quantity,
      this.total,
      this.dishOrder,
      this.modifiers.pluck('name').join(','),
    ].join(':')
  }

  get canBeRemoved () {
    // from Xef’s GSOrderContent.m @ canDelete
    if (this.alreadyPaidQuantity > 0) { // REV-5304
      return false
    }
    if ($vm.$page.props.brand === 'HANDY' && $vm.$store.getters['user/can']('cancelOrderContent')) { // REV-8118
      return true
    }
    // if (! APP.workingMethod.isWithTables) { // TO-DO ?
    //   return false
    // }
    return this.alreadyPaidQuantity == 0 && this.alreadyPrintedQuantity == 0
  }

  get printStatus () {
    return CalculatePrintStatus(this)
  }

  // Inspired by FrancePrintable.swift in XEF's APP
  get lines () {
    return 1
      + this._modifiersLines(this.modifiers)
      + this.subContents.reduce((acc, { modifiers }) => acc + 1 + this._modifiersLines(modifiers), 0)
  }

  _modifiersLines (modifiers) {
    return modifiers.filter(modifier => Number(modifier.price).greaterThan(0)).length
  }

  recalculate () {
    this._calculatePriceWithExtras()
    this._calculateDiscount()

    const amount = this._priceWithExtras - this.discountAmount
    if (!! $vm.$store.getters['rummage/business']('taxIncluded')) {
      this.total = amount.roundTo(2)
      this.subtotal = this._subtotal().roundTo(4)
    } else {
      this.subtotal = amount.roundTo(4)
      this.total = this._total().roundTo(2)
    }

    this.taxes.length
      ? this._applyTaxes()
      : this._applyNoTax(amount)

    this.taxAmount = this.appliedTaxes.sum('tax_amount').roundTo(4) 

    return this
  }

  // copied from XEF's APP ContentTaxesCalculator
  _applyTaxes () {
    this.taxes.forEach(tax => {
      let appliedTax = this.appliedTaxes.find(o => o.tax_id == tax.id)
      if (! appliedTax) {
        appliedTax = new AppliedTax({
          tax_id: tax.id,
          tax_name: tax.name,
          tax_percentage: tax.percentage,
        })
        this.appliedTaxes.push(appliedTax)
      }
      appliedTax.base_amount = this.subtotal
      appliedTax.tax_amount = (this.total - this.subtotal).roundTo(4)
    })
  }

  _applyNoTax (amount) {
    let appliedTax = this.appliedTaxes.find(o => ! o.tax_id)
    if (! appliedTax) {
      appliedTax = new AppliedTax()
      this.appliedTaxes.push(appliedTax)
    }
    appliedTax.base_amount = amount
  }

  _calculatePriceWithExtras () {
    if (this.item.type == MenuItemType.MENU) {
      this.extrasAmount = this.subContents.sum('priceWithExtrasIndividual') * Math.sign(this.quantity)
      this.priceWithExtrasIndividual = this.itemPrice + this.extrasAmount / this.quantity
    } else {
      // optional_modifiers
      // this.extrasAmount = [...this.modifiers, ...this.optional_modifiers].sum('price') + (this.orderContentCombination?.price ?? 0)
      this.extrasAmount = this.modifiers.sum('price') + (this.orderContentCombination?.price ?? 0)
      this.priceWithExtrasIndividual = this.itemPrice + this.extrasAmount
    }
    this.extrasAmount = Math.roundToCurrency(this.extrasAmount)
    this.priceWithExtrasIndividual = Math.roundToCurrency(this.priceWithExtrasIndividual)
    this._priceWithExtras = this._getPriceWithExtras()
  }

  _getPriceWithExtras () {
    // return this.item.usesWeight
    //   ? Math.roundToCurrency(this.priceWithExtrasIndividual * this.quantity * this.weight)
    //   : this.priceWithExtrasIndividual * this.quantity
    return this.priceWithExtrasIndividual * this.quantity
  }

  _calculateDiscount () {
    if (! this.discount) {
      this.discountAmount = 0
      return
    }

    let priceWithExtras = !! this.discount.withoutExtras ? this.itemPrice : this._getPriceWithModifiers()
    // if (this.item.usesWeight) {
    //   priceWithExtras *= this.weight
    // }

    const individualDiscountAmount = this._calculateDiscountForPrice(priceWithExtras)
    this.discountAmount = (individualDiscountAmount * this.quantity).roundTo(4)
  }

  _getPriceWithModifiers () {
    if (this.item.type == MenuItemType.MENU) {
      return this._priceWithExtras / this.quantity
    }
    return this.priceWithExtrasIndividual
  }

  _calculateDiscountForPrice (initialPrice) {
    const discountAmount = Number(!! this.discount.isPercentage
      ? (initialPrice * this.discount.discount / 100)
      : this.discount.discount
    )
    return discountAmount.greaterThan(initialPrice) ? initialPrice : discountAmount
  }

  _subtotal () {
    return this.total / this._taxFactor()
  }

  _total () {
    return this.subtotal * this._taxFactor()
  }

  _taxFactor () {
    this.taxPercentage = this.tax.percentage
    if (!! this.tax.id) {
      this.tax_id = this.tax.id
    }

    return 1 + this.taxPercentage / 100
  }

  increment () {
    this.quantity++
    this.$log('OrderContent', `Updated quantity (+1): ${this.quantity}`)
    return this.recalculate()
  }

  decrement () {
    if (this.quantity <= 1) {
      return this
    }
    this.quantity--
    this.$log('OrderContent', `Updated quantity (-1): ${this.quantity}`)
    return this.recalculate()
  }

  update (attributes) {
    this.notes = attributes.notes
    this._updateDiscount(attributes.discount)
    this._updateDishOrder(attributes.dishOrder)
    if (attributes.modifiers) {
      this._setModifiers(attributes.modifiers)
    }
    if (attributes.subContents) {
      this._setSubContents(attributes.subContents)
    }
    if (attributes.quantity) {
      this.quantity = attributes.quantity
    }
    return this.recalculate()
  }

  _updateDiscount (v) {
    if (this.discount?.id != v?.id) {
      this.$log('OrderContent', `Updated content discount: ${v?.id ?? 'NULL'} (${v?.discount ?? 0}${!! v?.isPercentage ? '%' : ''})`)
    }
    this.discount = v ?? null
  }

  _updateDishOrder (v) {
    if (this.dishOrder != v) {
      this.$log('OrderContent', `Updated dish order: ${v}`)
    }
    this.dishOrder = v ?? 0
  }

  isSameAs (other) {
    // Cases when not to stack items (copied from the Xef's APP)
    if (this.item_id != other.item_id) return false
    if (this.alreadyPaidQuantity !== 0) return false
    if (this.item.type == MenuItemType.MENU) return false
    if (this.notes !== null && this.notes != '') return false
    if (this.discount !== null) return false
    if (Number(this.itemPrice).notEqualTo(other.itemPrice)) return false
    if (this.item.isOpen) return false
    if (this.item.usesWeight) return false
    // if (this.seat != other.seat) return false
    if (this.dishOrder != other.dishOrder) return false

    if ($vm.$store.getters['orders/config/shouldStackItemsSameEmployee'] && this.tenantUser_id != other.tenantUser_id) return false

    if (this.format_pivot_id != other.format_pivot_id) return false
    if (this.orderContentCombination?.combination_id != other.orderContentCombination?.combination_id) return false

    if (! this.modifiers.pluck('id').isExactly(other.modifiers.pluck('id'))) return false
    return true
  }

  getFormattedSubContents (dividers = true) {
    // This part of the code attempts to emulate the "printMenuMenu" method of
    // the GSOrderItemCell.m file in XEF's APP. It clones the subContents array
    // without the ID, sorting and grouping it by "dishOrder". Then searches and
    // counts the repeated items in each group.
    if (! this.subContents?.length) {
      return []
    }

    const categories = $vm.$store.getters['catalog/menuCategories'](this.item)
    const grouped = Object.values(this.subContents.map(o => {
        const subContent = {
          ...o,
          course: categories.find(category => category.id === o.dishOrder)?.order ?? 0,
        }
        delete subContent.id
        return subContent
      })
      .sortByString('itemName')
      .groupBy('dishOrder')
    ).sort((a, b) => a[0].course - b[0].course)

    const list = []
    grouped.forEach((group, index, arr) => {
      const strs = []
      const objs = []
      group.forEach(subContent => {
        const str = JSON.stringify(subContent)
        const indexOf = strs.indexOf(str)
        if (indexOf === -1) {
          strs.push(str)
          objs.push({ ...JSON.parse(str), quantity: 1 })
        } else {
          objs[indexOf].quantity++
        }
      })
      list.push(objs)
      if (dividers && index < arr.length - 1) {
        list.push('divider')
      }
    })

    return list.flat()
  }

  toJSON () {
    delete this._item
    delete this._tax
    delete this._taxes
    delete this._priceWithExtras

    return this
  }
}
